import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import {
  Columns3,
  Download,
  FileSearch,
  LogOut,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { StatusPill } from "./components/ui/status-pill";
import {
  COLUMNS_KEY,
  DISCOVERY_DEFAULT_BRIEF,
  DISCOVERY_LIMIT,
  DISCOVERY_MAX_ATTEMPTS,
  DISCOVERY_MIN_SCORE,
  SELECTED_KEY,
  SPREADSHEET_BATCH_SIZE,
  defaultListColumnKeys,
  hiddenStatuses,
  listColumns,
  personFields,
  priorityOrder,
} from "./lib/constants";
import { canShowCrm, getBrowserConfig } from "./lib/config";
import {
  isLoginCodeComplete,
  loginCodeMaxLength,
  loginErrorMessage,
  matchesConfiguredLoginEmailHash,
  normalizeLoginCode,
  normalizeLoginEmail,
} from "./lib/login";
import { createLocalDataClient, createSupabaseDataClient, type DataClient } from "./lib/dataClient";
import { createStorage, type CrmStorage } from "./lib/storage";
import {
  buildSignalSummary,
  compactMeta,
  dateValue,
  discoveryExclusionRecords,
  discoveryResult,
  discoveryResultForPrompt,
  discoverySummaryMessage,
  exportRecordsCsv,
  filterRecords,
  findDuplicateRecord,
  firstEmail,
  fitScoreLabel,
  getActiveListColumns,
  gridStyleForColumns,
  isFollowUpDue,
  isUnreviewedRecord,
  lastActivity,
  listColumnValue,
  listSummaryLabel,
  mergeDiscoveryResults,
  newDiscoveryCount,
  normalizeDiscoveryCandidate,
  normalizeListColumnKeys,
  normalizePeople,
  normalizeRecord,
  parseInvestorCsv,
  recordPatchWithDerivedFields,
  researchListLabel,
  researchUpdatedLabel,
  safeUrl,
  sortDiscoveryResults,
  spreadsheetSeedRecord,
  spreadsheetSeedSummary,
  spreadsheetSummaryMessage,
  startOfToday,
  statusValues,
  toDatabaseRecord,
  toIsoDate,
  uniqueSpreadsheetRows,
  uniqueValues,
  fromDatabaseRecord,
  pipelineMetrics,
} from "./lib/records";
import type {
  DataMode,
  DiscoveryCandidate,
  DiscoveryRequest,
  DiscoveryResponse,
  DiscoveryResult,
  FilterState,
  InvestorField,
  InvestorRecord,
  MessageState,
  Person,
  ResearchResponse,
  SortKey,
  SpreadsheetRow,
  ViewKey,
} from "./types";

interface ResearchMessage {
  state: MessageState;
  message: string;
}

interface ResearchQueueState {
  running: boolean;
  total: number;
  done: number;
  activeId: string;
}

interface DiscoveryUiState {
  running: boolean;
  message: string;
  messageState: MessageState;
  results: DiscoveryResult[];
}

interface SpreadsheetUiState {
  rows: SpreadsheetRow[];
  running: boolean;
  message: string;
  messageState: MessageState;
}

const initialFilters: FilterState = {
  query: "",
  status: "visible",
  type: "all",
  priority: "all",
  sort: "activity",
  followUpDate: "",
};

const idleLoginMessage = "";
const personalEmailOnlyMessage = "Use your personal email for this CRM.";

export function App() {
  const [config] = useState(getBrowserConfig);
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(config.dataMode !== "supabase");
  const [pendingLoginEmail, setPendingLoginEmail] = useState("");
  const [loginMessage, setLoginMessage] = useState(() =>
    config.dataMode === "missing"
      ? "Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY before deploying."
      : "Checking session",
  );
  const [loginState, setLoginState] = useState<MessageState>(config.dataMode === "missing" ? "error" : "pending");
  const loginRequestInFlightRef = useRef(false);

  const supabase = useMemo(() => {
    if (!config.supabaseUrl || !config.supabaseKey) return null;
    return createClient(config.supabaseUrl, config.supabaseKey);
  }, [config.supabaseKey, config.supabaseUrl]);

  const dataClient = useMemo<DataClient | null>(() => {
    if (config.dataMode === "local") return createLocalDataClient(config.localRestUrl);
    if (config.dataMode === "supabase" && supabase) return createSupabaseDataClient(supabase);
    return null;
  }, [config.dataMode, config.localRestUrl, supabase]);

  useEffect(() => {
    if (config.dataMode === "local") {
      setAuthReady(true);
      setLoginMessage("Local Docker mode");
      setLoginState("ready");
      return;
    }

    if (config.dataMode === "missing" || !supabase) {
      setAuthReady(true);
      setLoginState("error");
      return;
    }

    let cancelled = false;
    supabase.auth.getSession().then(({ data, error }) => {
      if (cancelled) return;
      if (error) {
        setLoginMessage(error.message || "Could not read the current session.");
        setLoginState("error");
      } else {
        setSession(data.session);
        setLoginMessage(data.session ? "Signed in" : idleLoginMessage);
        setLoginState("ready");
      }
      setAuthReady(true);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      if (nextSession) setPendingLoginEmail("");
      setLoginMessage(nextSession ? "Signed in" : idleLoginMessage);
      setLoginState("ready");
      setAuthReady(true);
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, [config.dataMode, supabase]);

  const signIn = useCallback(
    async (email: string) => {
      if (config.dataMode === "local") return;
      if (!supabase) return;
      if (loginRequestInFlightRef.current) return;

      loginRequestInFlightRef.current = true;
      try {
        const normalizedEmail = normalizeLoginEmail(email);
        if (!normalizedEmail) {
          setLoginMessage("Enter your personal email address.");
          setLoginState("error");
          return;
        }

        if (
          config.loginEmailHash &&
          !(await matchesConfiguredLoginEmailHash(normalizedEmail, config.loginEmailHash))
        ) {
          setLoginMessage(personalEmailOnlyMessage);
          setLoginState("error");
          return;
        }

        setLoginMessage("Sending sign-in code");
        setLoginState("pending");

        const { error } = await supabase.auth.signInWithOtp({
          email: normalizedEmail,
          options: {
            shouldCreateUser: false,
          },
        });

        if (error) {
          setLoginMessage(loginErrorMessage(error, "Could not send the sign-in code."));
          setLoginState("error");
          return;
        }
        setPendingLoginEmail(normalizedEmail);
        setLoginMessage("Enter the code from your email. It expires in 2 minutes.");
        setLoginState("ready");
      } catch (error) {
        setLoginMessage(loginErrorMessage(error as { message?: string }, "Could not send the sign-in code."));
        setLoginState("error");
      } finally {
        loginRequestInFlightRef.current = false;
      }
    },
    [config.dataMode, config.loginEmailHash, supabase],
  );

  const verifyCode = useCallback(
    async (code: string) => {
      if (config.dataMode === "local") return;
      if (!supabase || !pendingLoginEmail) return;

      const token = normalizeLoginCode(code);
      if (!isLoginCodeComplete(token)) {
        setLoginMessage("Enter the full code from your email.");
        setLoginState("error");
        return;
      }
      if (loginRequestInFlightRef.current) return;

      loginRequestInFlightRef.current = true;
      try {
        setLoginMessage("Checking code");
        setLoginState("pending");
        const { data, error } = await supabase.auth.verifyOtp({
          email: pendingLoginEmail,
          token,
          type: "email",
        });

        if (error) {
          setLoginMessage(loginErrorMessage(error, "Could not verify the code."));
          setLoginState("error");
          return;
        }

        if (!data.session) {
          setLoginMessage("Could not create a session. Request a new code.");
          setLoginState("error");
          return;
        }

        setSession(data.session);
        setPendingLoginEmail("");
        setLoginMessage("Signed in");
        setLoginState("ready");
      } catch (error) {
        setLoginMessage(loginErrorMessage(error as { message?: string }, "Could not verify the code."));
        setLoginState("error");
      } finally {
        loginRequestInFlightRef.current = false;
      }
    },
    [config.dataMode, pendingLoginEmail, supabase],
  );

  const resendCode = useCallback(async () => {
    if (!pendingLoginEmail) return;
    await signIn(pendingLoginEmail);
  }, [pendingLoginEmail, signIn]);

  const resetLogin = useCallback(() => {
    setPendingLoginEmail("");
    setLoginMessage(idleLoginMessage);
    setLoginState("ready");
  }, []);

  const signOut = useCallback(async () => {
    if (config.dataMode === "local" || !supabase) return;
    resetLogin();
    await supabase.auth.signOut();
  }, [config.dataMode, resetLogin, supabase]);

  const hasAccess = authReady && canShowCrm(config.dataMode, Boolean(session));

  if (!hasAccess || !dataClient) {
    return (
      <AuthView
        message={loginMessage}
        state={loginState}
        pendingEmail={pendingLoginEmail}
        onSignIn={signIn}
        onVerifyCode={verifyCode}
        onResendCode={resendCode}
        onChangeEmail={resetLogin}
      />
    );
  }

  return (
    <CrmWorkspace
      dataMode={config.dataMode}
      localAiUrl={config.localAiUrl}
      dataClient={dataClient}
      supabase={supabase}
      session={session}
      onSignOut={signOut}
    />
  );
}

function AuthView({
  message,
  state,
  pendingEmail,
  onSignIn,
  onVerifyCode,
  onResendCode,
  onChangeEmail,
}: {
  message: string;
  state: MessageState;
  pendingEmail: string;
  onSignIn: (email: string) => Promise<void>;
  onVerifyCode: (code: string) => Promise<void>;
  onResendCode: () => Promise<void>;
  onChangeEmail: () => void;
}) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const isCodeStep = Boolean(pendingEmail);
  const isPending = state === "pending";
  const statusMessage = message.trim();

  useEffect(() => {
    if (!pendingEmail) return;
    setEmail(pendingEmail);
    setCode("");
  }, [pendingEmail]);

  return (
    <main className="auth-view">
      <form
        className="login-panel"
        onSubmit={(event) => {
          event.preventDefault();
          if (isPending) return;
          if (isCodeStep) {
            void onVerifyCode(code);
          } else {
            void onSignIn(email);
          }
        }}
      >
        <p className="eyebrow">Private CRM</p>
        <h1>Dan Frick CRM</h1>
        <label className="field full-width">
          <span>Email</span>
          <input
            type="email"
            autoComplete="email"
            placeholder="personal@example.com"
            readOnly={isCodeStep}
            disabled={isPending}
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        {isCodeStep ? (
          <label className="field full-width">
            <span>Code</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="12345678"
              maxLength={loginCodeMaxLength}
              required
              autoFocus
              disabled={isPending}
              value={code}
              onChange={(event) => setCode(normalizeLoginCode(event.target.value))}
            />
          </label>
        ) : null}
        <div className="login-actions">
          <Button variant="primary" type="submit" disabled={isPending}>
            {isCodeStep ? "Verify code" : "Send code"}
          </Button>
          {isCodeStep ? (
            <>
              <Button
                variant="secondary"
                type="button"
                disabled={isPending}
                onClick={() => {
                  setCode("");
                  void onResendCode();
                }}
              >
                Send new code
              </Button>
              <Button
                variant="secondary"
                type="button"
                disabled={isPending}
                onClick={() => {
                  setCode("");
                  onChangeEmail();
                }}
              >
                Change email
              </Button>
            </>
          ) : null}
        </div>
        {statusMessage ? (
          <span className="sync-pill login-status" data-state={state}>
            {statusMessage}
          </span>
        ) : null}
      </form>
    </main>
  );
}

function CrmWorkspace({
  dataMode,
  localAiUrl,
  dataClient,
  supabase,
  session,
  onSignOut,
}: {
  dataMode: DataMode;
  localAiUrl: string;
  dataClient: DataClient;
  supabase: SupabaseClient | null;
  session: Session | null;
  onSignOut: () => Promise<void>;
}) {
  const storage = useMemo(createStorage, []);
  const today = useMemo(startOfToday, []);
  const [view, setView] = useState<ViewKey>("crm");
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [records, setRecordsState] = useState<InvestorRecord[]>([]);
  const recordsRef = useRef<InvestorRecord[]>([]);
  const [selectedIdState, setSelectedIdState] = useState(() => storage.getItem(SELECTED_KEY) || "");
  const selectedIdRef = useRef(selectedIdState);
  const [activeListColumns, setActiveListColumnsState] = useState(() => loadListColumns(storage));
  const databaseReadyRef = useRef(false);
  const [dbStatus, setDbStatus] = useState("Connecting");
  const [dbStatusState, setDbStatusState] = useState<MessageState>("pending");
  const saveTimers = useRef(new Map<string, number>());
  const syncTimer = useRef<number | null>(null);
  const [researchMessages, setResearchMessages] = useState(new Map<string, ResearchMessage>());
  const [researchQueue, setResearchQueue] = useState<ResearchQueueState>({
    running: false,
    total: 0,
    done: 0,
    activeId: "",
  });
  const researchQueueRef = useRef(researchQueue);
  const [discoveryBrief, setDiscoveryBrief] = useState(DISCOVERY_DEFAULT_BRIEF);
  const [discoverySeeds, setDiscoverySeeds] = useState("");
  const [discoveryState, setDiscoveryState] = useState<DiscoveryUiState>({
    running: false,
    message: "Ready",
    messageState: "ready",
    results: [],
  });
  const [spreadsheetState, setSpreadsheetState] = useState<SpreadsheetUiState>({
    rows: [],
    running: false,
    message: "No spreadsheet selected",
    messageState: "ready",
  });

  useEffect(() => {
    researchQueueRef.current = researchQueue;
  }, [researchQueue]);

  const setRecords = useCallback((nextRecords: InvestorRecord[]) => {
    recordsRef.current = nextRecords;
    setRecordsState(nextRecords);
  }, []);

  const setSelectedId = useCallback(
    (id: string) => {
      selectedIdRef.current = id;
      setSelectedIdState(id);
      if (id) {
        storage.setItem(SELECTED_KEY, id);
      } else {
        storage.removeItem(SELECTED_KEY);
      }
    },
    [storage],
  );

  const setDatabaseReady = useCallback((ready: boolean) => {
    databaseReadyRef.current = ready;
  }, []);

  const setDatabaseStatus = useCallback((label: string, state: MessageState) => {
    setDbStatus(label);
    setDbStatusState(state);
  }, []);

  const markSavedSoon = useCallback(() => {
    if (syncTimer.current) window.clearTimeout(syncTimer.current);
    syncTimer.current = window.setTimeout(() => {
      if (databaseReadyRef.current) {
        setDatabaseStatus(dataClient.label, "ready");
      }
    }, 900);
  }, [dataClient.label, setDatabaseStatus]);

  const loadDatabaseRecords = useCallback(async () => {
    setDatabaseStatus("Connecting", "pending");
    try {
      const rows = await dataClient.loadRecords();
      const loadedRecords = rows.map(fromDatabaseRecord);
      setRecords(loadedRecords);
      setDatabaseReady(true);

      const currentSelected = selectedIdRef.current;
      if (!loadedRecords.some((record) => record.id === currentSelected)) {
        setSelectedId(
          loadedRecords.find((record) => !hiddenStatuses.has(record.status))?.id ||
            loadedRecords[0]?.id ||
            "",
        );
      }

      setDatabaseStatus(dataClient.label, "ready");
    } catch (error) {
      setRecords([]);
      setDatabaseReady(false);
      setDatabaseStatus("Database offline", "error");
      console.error(error);
    }
  }, [dataClient, setDatabaseReady, setDatabaseStatus, setRecords, setSelectedId]);

  useEffect(() => {
    void loadDatabaseRecords();
  }, [loadDatabaseRecords]);

  const persistRecord = useCallback(
    (record: InvestorRecord, immediate = false) => {
      if (!databaseReadyRef.current) return;

      const save = async () => {
        try {
          setDatabaseStatus("Saving", "pending");
          await dataClient.updateRecord(record.id, toDatabaseRecord(record));
          setDatabaseStatus("Saved", "ready");
          markSavedSoon();
        } catch (error) {
          setDatabaseStatus("Save failed", "error");
          console.error(error);
        }
      };

      window.clearTimeout(saveTimers.current.get(record.id));
      if (immediate) {
        void save();
        return;
      }
      saveTimers.current.set(record.id, window.setTimeout(save, 350));
    },
    [dataClient, markSavedSoon, setDatabaseStatus],
  );

  const createDatabaseRecord = useCallback(
    async (record: InvestorRecord) => {
      if (!databaseReadyRef.current) return;
      try {
        setDatabaseStatus("Saving", "pending");
        await dataClient.createRecord(toDatabaseRecord(record));
        setDatabaseStatus("Saved", "ready");
        markSavedSoon();
      } catch (error) {
        setDatabaseStatus("Save failed", "error");
        console.error(error);
      }
    },
    [dataClient, markSavedSoon, setDatabaseStatus],
  );

  const deleteDatabaseRecord = useCallback(
    async (id: string) => {
      if (!databaseReadyRef.current) return;
      try {
        setDatabaseStatus("Saving", "pending");
        await dataClient.deleteRecord(id);
        setDatabaseStatus("Saved", "ready");
        markSavedSoon();
      } catch (error) {
        setDatabaseStatus("Delete failed", "error");
        console.error(error);
      }
    },
    [dataClient, markSavedSoon, setDatabaseStatus],
  );

  const patchRecord = useCallback(
    (id: string, changes: Partial<InvestorRecord>, immediate = true): InvestorRecord | null => {
      let updatedRecord: InvestorRecord | null = null;
      const nextRecords = recordsRef.current.map((record) => {
        if (record.id !== id) return record;
        updatedRecord = recordPatchWithDerivedFields(record, changes);
        return updatedRecord;
      });
      setRecords(nextRecords);
      if (updatedRecord) persistRecord(updatedRecord, immediate);
      return updatedRecord;
    },
    [persistRecord, setRecords],
  );

  const visibleRecords = useMemo(() => filterRecords(records, filters), [records, filters]);
  const selectedRecord = records.find((record) => record.id === selectedIdState) || null;

  useEffect(() => {
    if (!records.length) {
      if (selectedIdState) setSelectedId("");
      return;
    }

    const selectedIsVisible = visibleRecords.some((record) => record.id === selectedIdState);
    if (visibleRecords.length > 0 && !selectedIsVisible) {
      setSelectedId(visibleRecords[0].id);
    } else if (!records.some((record) => record.id === selectedIdState)) {
      setSelectedId(visibleRecords[0]?.id || records[0]?.id || "");
    }
  }, [records, selectedIdState, setSelectedId, visibleRecords]);

  const setListColumn = useCallback(
    (key: string, checked: boolean) => {
      const column = listColumns.find((item) => item.key === key);
      if (!column || (column.required && !checked)) return;

      const selected = new Set(activeListColumns);
      if (checked) {
        selected.add(key);
      } else {
        selected.delete(key);
      }

      const normalized = normalizeListColumnKeys([...selected]);
      setActiveListColumnsState(normalized);
      storage.setItem(COLUMNS_KEY, JSON.stringify(normalized));
    },
    [activeListColumns, storage],
  );

  const authenticatedJsonHeaders = useCallback(async (): Promise<Record<string, string>> => {
    if (dataMode === "local") {
      return { "Content-Type": "application/json" };
    }
    if (!supabase) {
      throw new Error("Sign in again before using AI research.");
    }

    const { data, error } = await supabase.auth.getSession();
    const token = data.session?.access_token || session?.access_token;
    if (error || !token) {
      throw new Error("Sign in again before using AI research.");
    }
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, [dataMode, session?.access_token, supabase]);

  const aiEndpoint = useCallback(
    (name: "research" | "discover") =>
      dataMode === "local"
        ? `${localAiUrl.replace(/\/+$/, "")}/${name}`
        : `/api/${name}`,
    [dataMode, localAiUrl],
  );

  const requestAiResearch = useCallback(
    async (record: InvestorRecord): Promise<ResearchResponse> => {
      let response: Response;
      try {
        response = await fetch(aiEndpoint("research"), {
          method: "POST",
          headers: await authenticatedJsonHeaders(),
          body: JSON.stringify({ record }),
        });
      } catch (error) {
        throw new Error("AI research is not available. Check the CRM server function configuration.", { cause: error });
      }

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || `${response.status} ${response.statusText}`);
      }
      return payload as ResearchResponse;
    },
    [aiEndpoint, authenticatedJsonHeaders],
  );

  const requestAiDiscovery = useCallback(
    async (payload: DiscoveryRequest): Promise<DiscoveryResponse> => {
      let response: Response;
      try {
        response = await fetch(aiEndpoint("discover"), {
          method: "POST",
          headers: await authenticatedJsonHeaders(),
          body: JSON.stringify(payload),
        });
      } catch (error) {
        throw new Error("AI discovery is not available. Check the CRM server function configuration.", { cause: error });
      }

      const responsePayload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responsePayload.error || `${response.status} ${response.statusText}`);
      }
      return responsePayload as DiscoveryResponse;
    },
    [aiEndpoint, authenticatedJsonHeaders],
  );

  const setResearchMessage = useCallback((id: string, state: MessageState, message: string) => {
    setResearchMessages((previous) => {
      const next = new Map(previous);
      next.set(id, { state, message });
      return next;
    });
  }, []);

  const researchRecord = useCallback(
    async (record: InvestorRecord) => {
      setResearchMessage(record.id, "pending", "Researching");
      try {
        const result = await requestAiResearch(record);
        patchRecord(record.id, {
          aiFitScore: result.fitScore ? String(result.fitScore) : "",
          people: normalizePeople(result.people || []),
          aiResearch: result.summary || "",
          aiResearchSources: (result.sources || []).join("\n"),
          aiResearchedAt: result.researchedAt || new Date().toISOString(),
        });
        setResearchMessage(
          record.id,
          "ready",
          databaseReadyRef.current ? "Research saved" : "Research stored for this session",
        );
      } catch (error) {
        setResearchMessage(record.id, "error", error instanceof Error ? error.message : "Research failed");
        console.error(error);
      }
    },
    [patchRecord, requestAiResearch, setResearchMessage],
  );

  const researchRecords = useCallback(
    async (recordsToResearch: InvestorRecord[], isBatch: boolean) => {
      setResearchQueue({ running: true, total: recordsToResearch.length, done: 0, activeId: "" });

      let done = 0;
      for (const record of recordsToResearch) {
        const current = recordsRef.current.find((item) => item.id === record.id);
        if (!current) continue;

        setResearchQueue({ running: true, total: recordsToResearch.length, done, activeId: current.id });
        await researchRecord(current);
        done += 1;
        setResearchQueue({ running: true, total: recordsToResearch.length, done, activeId: current.id });
      }

      setResearchQueue({ running: false, total: recordsToResearch.length, done, activeId: "" });
      if (isBatch) {
        setDatabaseStatus("Research complete", databaseReadyRef.current ? "ready" : "error");
        markSavedSoon();
      }
    },
    [markSavedSoon, researchRecord, setDatabaseStatus],
  );

  const researchSelectedRecord = useCallback(async () => {
    const record = recordsRef.current.find((item) => item.id === selectedIdRef.current);
    if (!record || researchQueueRef.current.running) return;
    await researchRecords([record], false);
  }, [researchRecords]);

  const researchUnreviewedRecords = useCallback(async () => {
    const visibleUnreviewed = filterRecords(recordsRef.current, filters).filter(isUnreviewedRecord);
    if (visibleUnreviewed.length === 0 || researchQueueRef.current.running) return;

    const confirmed = window.confirm(
      `Research ${visibleUnreviewed.length} unreviewed contacts with AI? This can use one web search request per contact.`,
    );
    if (!confirmed) return;
    await researchRecords(visibleUnreviewed, true);
  }, [filters, researchRecords]);

  const saveDiscoveryCandidates = useCallback(
    async (candidates: DiscoveryCandidate[], query: string, searchedAt?: string) => {
      const results: DiscoveryResult[] = [];
      const skippedDuplicateNames: string[] = [];
      let skippedUnusableCount = 0;
      const foundAt = searchedAt || new Date().toISOString();

      for (const candidate of candidates) {
        const candidateRecord = normalizeDiscoveryCandidate(candidate, query, foundAt);
        if (!candidateRecord.firm && !candidateRecord.contact) {
          skippedUnusableCount += 1;
          continue;
        }
        const existing = findDuplicateRecord(recordsRef.current, candidateRecord);
        if (existing) {
          skippedDuplicateNames.push(existing.name || candidateRecord.name);
          continue;
        }

        const nextRecords = [candidateRecord, ...recordsRef.current];
        setRecords(nextRecords);
        await createDatabaseRecord(candidateRecord);
        results.push(discoveryResult(candidateRecord, candidateRecord.status === "Filtered Out" ? "Filtered out" : "Added", candidateRecord));
      }

      return {
        results,
        skippedDuplicateNames: [...new Set(skippedDuplicateNames.filter(Boolean))],
        skippedUnusableCount,
      };
    },
    [createDatabaseRecord, setRecords],
  );

  const runDiscovery = useCallback(async () => {
    if (discoveryState.running || spreadsheetState.running) return;

    const query = discoveryBrief.trim() || DISCOVERY_DEFAULT_BRIEF;
    const seedText = discoverySeeds.trim();
    setDiscoveryState({ running: true, message: "Discovering investors", messageState: "pending", results: [] });

    try {
      let results: DiscoveryResult[] = [];
      let skippedDuplicateNames: string[] = [];
      let attempt = 1;

      while (attempt <= DISCOVERY_MAX_ATTEMPTS && newDiscoveryCount(results) < DISCOVERY_LIMIT) {
        const needed = DISCOVERY_LIMIT - newDiscoveryCount(results);
        setDiscoveryState({
          running: true,
          message: attempt === 1 ? "Discovering investors" : `Looking for ${needed} more new investors`,
          messageState: "pending",
          results,
        });

        let payload: DiscoveryResponse;
        try {
          payload = await requestAiDiscovery({
            query,
            seedText,
            existingRecords: discoveryExclusionRecords(recordsRef.current),
            signalSummary: buildSignalSummary(recordsRef.current),
            previousDiscoveryResults: results.map(discoveryResultForPrompt),
            recentDuplicateNames: skippedDuplicateNames,
            attempt,
            targetNewCount: needed,
            limit: DISCOVERY_LIMIT,
            minimumFitScore: DISCOVERY_MIN_SCORE,
            geoPreference: "US first",
            prioritizeNew: true,
          });
        } catch (error) {
          if (results.length) break;
          throw error;
        }

        if (!Array.isArray(payload.candidates) || payload.candidates.length === 0) {
          if (results.length) break;
          throw new Error(
            payload.searchSummary
              ? `AI returned no discovery candidates: ${payload.searchSummary}`
              : "AI returned no discovery candidates. Try adding seed investor names or broadening the search brief.",
          );
        }

        const batch = await saveDiscoveryCandidates(payload.candidates, query, payload.searchedAt);
        const batchResults = batch.results;
        results = mergeDiscoveryResults(results, batchResults);
        skippedDuplicateNames = [...new Set([...skippedDuplicateNames, ...batch.skippedDuplicateNames])];

        if (newDiscoveryCount(batchResults) === 0 && attempt === DISCOVERY_MAX_ATTEMPTS) {
          break;
        }
        attempt += 1;
      }

      if (results.length === 0) {
        throw new Error(
          skippedDuplicateNames.length
            ? `Discovery only returned investors already in the CRM: ${skippedDuplicateNames.slice(0, 6).join(", ")}. Try adding seed names or broadening the search brief.`
            : "AI returned candidates, but none had a usable firm or contact name.",
        );
      }

      const sortedResults = sortDiscoveryResults(results);
      setDiscoveryState({
        running: false,
        results: sortedResults,
        message: discoverySummaryMessage(sortedResults, skippedDuplicateNames.length),
        messageState: "ready",
      });
      setDatabaseStatus(databaseReadyRef.current ? "Discovery saved" : "Discovery stored for this session", databaseReadyRef.current ? "ready" : "error");
      markSavedSoon();
    } catch (error) {
      setDiscoveryState((current) => ({
        ...current,
        running: false,
        message: error instanceof Error ? error.message : "Discovery failed",
        messageState: "error",
      }));
      console.error(error);
    }
  }, [
    discoveryBrief,
    discoverySeeds,
    discoveryState.running,
    markSavedSoon,
    requestAiDiscovery,
    saveDiscoveryCandidates,
    setDatabaseStatus,
    spreadsheetState.running,
  ]);

  const researchSpreadsheetRows = useCallback(async () => {
    if (spreadsheetState.running || discoveryState.running || spreadsheetState.rows.length === 0) return;

    setSpreadsheetState((current) => ({ ...current, running: true, messageState: "pending" }));
    setDiscoveryState((current) => ({ ...current, results: [] }));

    const query = [
      "Research the provided spreadsheet rows for RxexR investor fit.",
      `Return each identifiable seed row with an honest fit score; the CRM will add fit ${DISCOVERY_MIN_SCORE}+ rows as relevant and save lower-fit rows as Filtered Out memory.`,
      "Do not discover investors outside the spreadsheet rows for this import.",
    ].join(" ");
    const rows = uniqueSpreadsheetRows(spreadsheetState.rows);
    let results: DiscoveryResult[] = [];
    let skippedExisting = 0;
    let skippedUnusable = 0;
    let errors = 0;
    let processed = 0;

    try {
      for (let index = 0; index < rows.length; index += SPREADSHEET_BATCH_SIZE) {
        const chunk = rows.slice(index, index + SPREADSHEET_BATCH_SIZE);
        const rowsToResearch: SpreadsheetRow[] = [];

        for (const row of chunk) {
          if (findDuplicateRecord(recordsRef.current, spreadsheetSeedRecord(row))) {
            skippedExisting += 1;
          } else {
            rowsToResearch.push(row);
          }
        }

        processed += chunk.length;
        setSpreadsheetState((current) => ({
          ...current,
          message: `Researching spreadsheet ${Math.min(processed, rows.length)}/${rows.length}`,
          messageState: "pending",
        }));

        if (rowsToResearch.length === 0) {
          continue;
        }

        try {
          const payload = await requestAiDiscovery({
            query,
            seedText: rowsToResearch.map(spreadsheetSeedSummary).join("\n"),
            spreadsheetRows: rowsToResearch,
            seedMode: "spreadsheet_import",
            existingRecords: discoveryExclusionRecords(recordsRef.current),
            signalSummary: buildSignalSummary(recordsRef.current),
            previousDiscoveryResults: results.map(discoveryResultForPrompt),
            recentDuplicateNames: [],
            attempt: Math.floor(index / SPREADSHEET_BATCH_SIZE) + 1,
            targetNewCount: rowsToResearch.length,
            limit: rowsToResearch.length,
            minimumFitScore: DISCOVERY_MIN_SCORE,
            geoPreference: "US first",
            prioritizeNew: true,
          });
          const batch = await saveDiscoveryCandidates(payload.candidates || [], query, payload.searchedAt);
          results = mergeDiscoveryResults(results, batch.results);
          skippedExisting += batch.skippedDuplicateNames.length;
          skippedUnusable += batch.skippedUnusableCount;
          setDiscoveryState((current) => ({ ...current, results: sortDiscoveryResults(results) }));
        } catch (error) {
          errors += 1;
          console.error(error);
        }
      }

      const sortedResults = sortDiscoveryResults(results);
      setDiscoveryState({
        running: false,
        results: sortedResults,
        message: sortedResults.length
          ? `Spreadsheet added ${sortedResults.length} relevant funds`
          : "Spreadsheet import found no new relevant funds",
        messageState: sortedResults.length ? "ready" : "error",
      });
      setSpreadsheetState((current) => ({
        ...current,
        running: false,
        message: spreadsheetSummaryMessage({
          saved: sortedResults.length,
          skippedExisting,
          skippedUnusable,
          errors,
        }),
        messageState: errors && sortedResults.length === 0 ? "error" : "ready",
      }));
      setDatabaseStatus(databaseReadyRef.current ? "Spreadsheet import saved" : "Spreadsheet import stored for this session", databaseReadyRef.current ? "ready" : "error");
      markSavedSoon();
    } finally {
      setSpreadsheetState((current) => ({ ...current, running: false }));
    }
  }, [
    discoveryState.running,
    markSavedSoon,
    requestAiDiscovery,
    saveDiscoveryCandidates,
    setDatabaseStatus,
    spreadsheetState.rows,
    spreadsheetState.running,
  ]);

  const handleSpreadsheetFile = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSpreadsheetState({
        rows: [],
        running: false,
        message: "No spreadsheet selected",
        messageState: "ready",
      });
      return;
    }

    try {
      const text = await file.text();
      const rows = parseInvestorCsv(text);
      setSpreadsheetState({
        rows,
        running: false,
        message: `${rows.length} funds loaded from ${file.name}`,
        messageState: rows.length ? "ready" : "error",
      });
    } catch (error) {
      setSpreadsheetState({
        rows: [],
        running: false,
        message: error instanceof Error ? error.message : "Could not parse spreadsheet",
        messageState: "error",
      });
    }
  }, []);

  const addContact = useCallback(() => {
    const record = normalizeRecord({
      id: `custom-${Date.now()}`,
      firm: "",
      contact: "New contact",
      type: "Angel",
      status: "Not Started",
    });
    setRecords([record, ...recordsRef.current]);
    setSelectedId(record.id);
    void createDatabaseRecord(record);
  }, [createDatabaseRecord, setRecords, setSelectedId]);

  const deleteSelectedRecord = useCallback(() => {
    const record = recordsRef.current.find((item) => item.id === selectedIdRef.current);
    if (!record) return;
    const confirmed = window.confirm(`Remove ${record.name} from this CRM?`);
    if (!confirmed) return;

    const nextRecords = recordsRef.current.filter((item) => item.id !== record.id);
    setRecords(nextRecords);
    setSelectedId(nextRecords[0]?.id || "");
    void deleteDatabaseRecord(record.id);
  }, [deleteDatabaseRecord, setRecords, setSelectedId]);

  const updateSelectedField = useCallback(
    (field: InvestorField, value: string, immediate = false) => {
      const id = selectedIdRef.current;
      if (!id) return;
      patchRecord(id, { [field]: value } as Partial<InvestorRecord>, immediate);
    },
    [patchRecord],
  );

  const addPerson = useCallback(() => {
    const record = recordsRef.current.find((item) => item.id === selectedIdRef.current);
    if (!record) return;
    patchRecord(record.id, {
      people: [
        ...normalizePeople(record.people),
        { name: "New person", title: "", linkedin: "", xProfile: "", email: "", notes: "" },
      ],
    });
  }, [patchRecord]);

  const updatePerson = useCallback(
    (index: number, field: keyof Person, value: string, immediate = false) => {
      if (!personFields.includes(field) || !Number.isInteger(index) || index < 0) return;
      const record = recordsRef.current.find((item) => item.id === selectedIdRef.current);
      if (!record) return;

      const people = normalizePeople(record.people);
      if (!people[index]) return;
      people[index] = { ...people[index], [field]: value.trimStart() };
      patchRecord(record.id, { people }, immediate);
    },
    [patchRecord],
  );

  const removePerson = useCallback(
    (index: number) => {
      if (!Number.isInteger(index) || index < 0) return;
      const record = recordsRef.current.find((item) => item.id === selectedIdRef.current);
      if (!record) return;
      patchRecord(record.id, {
        people: normalizePeople(record.people).filter((_, personIndex) => personIndex !== index),
      });
    },
    [patchRecord],
  );

  const logContactToday = useCallback(() => {
    const id = selectedIdRef.current;
    if (!id) return;
    patchRecord(id, { dateContacted: toIsoDate(today), status: "Contacted" });
  }, [patchRecord, today]);

  const clearFollowUp = useCallback(() => {
    const id = selectedIdRef.current;
    if (!id) return;
    patchRecord(id, { followUpDate: "" });
  }, [patchRecord]);

  const exportCsv = useCallback(() => {
    const { csv, filename } = exportRecordsCsv(recordsRef.current, today);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [today]);

  const openDiscoveryResult = useCallback(
    (id: string) => {
      const record = recordsRef.current.find((item) => item.id === id);
      if (!record) return;
      setSelectedId(id);
      if (record.status === "Filtered Out") {
        setFilters((current) => ({ ...current, status: "Filtered Out" }));
      }
      setView("crm");
    },
    [setSelectedId],
  );

  const visibleUnreviewedCount = visibleRecords.filter(isUnreviewedRecord).length;

  return (
    <div id="appShell">
      <header className="app-header">
        <div className="brand-block">
          <p className="eyebrow">Personal CRM</p>
          <h1>RxexR investor pipeline</h1>
        </div>
        <div className="header-actions">
          <Button active={view === "crm"} className="view-button" onClick={() => setView("crm")}>
            CRM
          </Button>
          <Button active={view === "discovery"} className="view-button" onClick={() => setView("discovery")}>
            Discovery
          </Button>
          <span className="sync-pill" data-state={dbStatusState}>
            {dbStatus}
          </span>
          <Button onClick={exportCsv}>
            <Download size={16} aria-hidden="true" />
            Export CSV
          </Button>
          {dataMode !== "local" ? (
            <Button onClick={() => void onSignOut()}>
              <LogOut size={16} aria-hidden="true" />
              Sign out
            </Button>
          ) : null}
        </div>
      </header>

      {view === "crm" ? (
        <>
          <MetricsStrip records={records} today={today} />
          <main className="crm-layout">
            <InvestorList
              records={records}
              visibleRecords={visibleRecords}
              selectedId={selectedIdState}
              filters={filters}
              activeListColumns={activeListColumns}
              visibleUnreviewedCount={visibleUnreviewedCount}
              researchQueue={researchQueue}
              today={today}
              onFiltersChange={setFilters}
              onSelect={setSelectedId}
              onSetListColumn={setListColumn}
              onAddContact={addContact}
              onResearchVisible={() => void researchUnreviewedRecords()}
            />
            <DetailInspector
              record={selectedRecord}
              records={records}
              today={today}
              researchMessage={selectedRecord ? researchMessages.get(selectedRecord.id) : undefined}
              researchQueue={researchQueue}
              onFieldChange={updateSelectedField}
              onLogContact={logContactToday}
              onClearFollowUp={clearFollowUp}
              onResearchSelected={() => void researchSelectedRecord()}
              onAddPerson={addPerson}
              onPersonChange={updatePerson}
              onRemovePerson={removePerson}
              onDelete={deleteSelectedRecord}
            />
          </main>
        </>
      ) : (
        <DiscoveryWorkbench
          discoveryBrief={discoveryBrief}
          discoverySeeds={discoverySeeds}
          discoveryState={discoveryState}
          spreadsheetState={spreadsheetState}
          onBriefChange={setDiscoveryBrief}
          onSeedsChange={setDiscoverySeeds}
          onFileChange={handleSpreadsheetFile}
          onResearchSpreadsheet={() => void researchSpreadsheetRows()}
          onRunDiscovery={() => void runDiscovery()}
          onOpenRecord={openDiscoveryResult}
        />
      )}
    </div>
  );
}

function loadListColumns(storage: CrmStorage): string[] {
  try {
    const stored = JSON.parse(storage.getItem(COLUMNS_KEY) || "null");
    if (Array.isArray(stored)) return normalizeListColumnKeys(stored);
  } catch {
    return [...defaultListColumnKeys];
  }
  return [...defaultListColumnKeys];
}

function MetricsStrip({ records, today }: { records: InvestorRecord[]; today: Date }) {
  return (
    <section className="metrics-strip" aria-label="Pipeline summary">
      {pipelineMetrics(records, today).map(([label, value]) => (
        <div className="metric" key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </section>
  );
}

function InvestorList({
  records,
  visibleRecords,
  selectedId,
  filters,
  activeListColumns,
  visibleUnreviewedCount,
  researchQueue,
  today,
  onFiltersChange,
  onSelect,
  onSetListColumn,
  onAddContact,
  onResearchVisible,
}: {
  records: InvestorRecord[];
  visibleRecords: InvestorRecord[];
  selectedId: string;
  filters: FilterState;
  activeListColumns: string[];
  visibleUnreviewedCount: number;
  researchQueue: ResearchQueueState;
  today: Date;
  onFiltersChange: (filters: FilterState | ((current: FilterState) => FilterState)) => void;
  onSelect: (id: string) => void;
  onSetListColumn: (key: string, checked: boolean) => void;
  onAddContact: () => void;
  onResearchVisible: () => void;
}) {
  const columns = getActiveListColumns(activeListColumns);
  const gridStyle = gridStyleForColumns(columns);

  return (
    <section className="list-pane" aria-label="Investor list">
      <div className="toolbar">
        <label className="search-field">
          <span>Search</span>
          <div className="input-with-icon">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              autoComplete="off"
              placeholder="Investor, firm, notes, source..."
              value={filters.query}
              onChange={(event) => onFiltersChange((current) => ({ ...current, query: event.target.value }))}
            />
          </div>
        </label>
        <div className="filter-grid">
          <label>
            Status
            <select
              value={filters.status}
              onChange={(event) => onFiltersChange((current) => ({ ...current, status: event.target.value }))}
            >
              <option value="visible">Visible statuses</option>
              <option value="all">All statuses</option>
              {statusValues(records).map((status) => (
                <option key={status} value={status}>
                  {status || "Unspecified"}
                </option>
              ))}
            </select>
          </label>
          <label>
            Type
            <select
              value={filters.type}
              onChange={(event) => onFiltersChange((current) => ({ ...current, type: event.target.value }))}
            >
              <option value="all">All types</option>
              {uniqueValues(records, "type").map((value) => (
                <option key={value} value={value}>
                  {value || "Unspecified"}
                </option>
              ))}
            </select>
          </label>
          <label>
            Priority
            <select
              value={filters.priority}
              onChange={(event) => onFiltersChange((current) => ({ ...current, priority: event.target.value }))}
            >
              <option value="all">All priorities</option>
              {uniqueValues(records, "priority", priorityOrder).map((value) => (
                <option key={value} value={value}>
                  {value || "Unspecified"}
                </option>
              ))}
            </select>
          </label>
          <label>
            Sort
            <select
              value={filters.sort}
              onChange={(event) => onFiltersChange((current) => ({ ...current, sort: event.target.value as SortKey }))}
            >
              <option value="activity">Recent activity</option>
              <option value="followup">Follow-up date</option>
              <option value="priority">Priority</option>
              <option value="name">Name</option>
              <option value="status">Status</option>
            </select>
          </label>
          <label>
            Follow-up Date
            <input
              type="date"
              value={filters.followUpDate}
              onChange={(event) => onFiltersChange((current) => ({ ...current, followUpDate: event.target.value }))}
            />
          </label>
        </div>
        <div className="toolbar-actions">
          <details className="column-chooser">
            <summary>
              <Columns3 size={16} aria-hidden="true" />
              Columns
            </summary>
            <div className="column-menu">
              {listColumns.map((column) => (
                <label className="column-option" key={column.key}>
                  <input
                    type="checkbox"
                    checked={activeListColumns.includes(column.key)}
                    disabled={column.required}
                    onChange={(event) => onSetListColumn(column.key, event.target.checked)}
                  />
                  <span>{column.label}</span>
                </label>
              ))}
            </div>
          </details>
          <Button variant="primary" onClick={onAddContact}>
            <Plus size={16} aria-hidden="true" />
            Add contact
          </Button>
          <Button disabled={researchQueue.running || visibleUnreviewedCount === 0} onClick={onResearchVisible}>
            <Sparkles size={16} aria-hidden="true" />
            {researchQueue.running
              ? `Researching ${researchQueue.done}/${researchQueue.total}`
              : `Research unreviewed (${visibleUnreviewedCount})`}
          </Button>
        </div>
      </div>
      <div className="list-summary">{listSummaryLabel(records, visibleRecords.length, filters.status)}</div>
      <div className="records-list">
        {visibleRecords.length === 0 ? (
          <div className="empty-state">
            <strong>No contacts found</strong>
            <span>Adjust search or filters.</span>
          </div>
        ) : (
          <>
            <div className="record-grid-header" style={gridStyle}>
              {columns.map((column) => (
                <span key={column.key}>{column.label}</span>
              ))}
            </div>
            {visibleRecords.map((record) => {
              const activity = lastActivity(record, today);
              const due = isFollowUpDue(record, today);
              return (
                <button
                  className={`record-row ${record.id === selectedId ? "is-selected" : ""}`}
                  style={gridStyle}
                  type="button"
                  key={record.id}
                  onClick={() => onSelect(record.id)}
                >
                  {columns.map((column) => (
                    <RecordColumnCell
                      key={column.key}
                      columnKey={column.key}
                      record={record}
                      activity={activity}
                      due={due}
                      today={today}
                    />
                  ))}
                </button>
              );
            })}
          </>
        )}
      </div>
    </section>
  );
}

function RecordColumnCell({
  columnKey,
  record,
  activity,
  due,
  today,
}: {
  columnKey: ReturnType<typeof getActiveListColumns>[number]["key"];
  record: InvestorRecord;
  activity: ReturnType<typeof lastActivity>;
  due: boolean;
  today: Date;
}) {
  if (columnKey === "name") {
    return (
      <span className="record-cell row-main">
        <span className="record-name">{record.name}</span>
        <span className="record-meta">{compactMeta(record)}</span>
      </span>
    );
  }

  if (columnKey === "status") {
    return (
      <span className="record-cell status-cell">
        <StatusPill status={record.status} />
      </span>
    );
  }

  if (columnKey === "activity") {
    return <span className={`record-cell activity-cell ${due ? "is-due" : ""}`}>{activity.label}</span>;
  }

  if (columnKey === "nextStep") {
    return <span className="record-cell list-cell next-cell">{record.nextStep || record.notes || "No next step"}</span>;
  }

  return <span className="record-cell list-cell">{listColumnValue(record, columnKey, today) || "Unspecified"}</span>;
}

function DetailInspector({
  record,
  records,
  today,
  researchMessage,
  researchQueue,
  onFieldChange,
  onLogContact,
  onClearFollowUp,
  onResearchSelected,
  onAddPerson,
  onPersonChange,
  onRemovePerson,
  onDelete,
}: {
  record: InvestorRecord | null;
  records: InvestorRecord[];
  today: Date;
  researchMessage?: ResearchMessage;
  researchQueue: ResearchQueueState;
  onFieldChange: (field: InvestorField, value: string, immediate?: boolean) => void;
  onLogContact: () => void;
  onClearFollowUp: () => void;
  onResearchSelected: () => void;
  onAddPerson: () => void;
  onPersonChange: (index: number, field: keyof Person, value: string, immediate?: boolean) => void;
  onRemovePerson: (index: number) => void;
  onDelete: () => void;
}) {
  if (!record) {
    return (
      <aside className="detail-pane" aria-label="Investor details">
        <div id="detailPanel">
          <div className="empty-state detail-empty">
            <strong>No contact selected</strong>
            <span>Add a contact or choose one from the list.</span>
          </div>
        </div>
      </aside>
    );
  }

  const activity = lastActivity(record, today);
  const url = safeUrl(record.url);
  const people = normalizePeople(record.people);

  return (
    <aside className="detail-pane" aria-label="Investor details">
      <div id="detailPanel">
        <div className="detail-header">
          <div>
            <p className="eyebrow">Selected investor</p>
            <h2>{record.name}</h2>
            <span className="detail-subtitle">{activity.description}</span>
          </div>
          <StatusPill status={record.status} />
        </div>

        <div className="detail-actions">
          <Button variant="primary" onClick={onLogContact}>
            Log contact today
          </Button>
          <Button onClick={onClearFollowUp}>Clear follow-up</Button>
          <Button variant="danger" onClick={onDelete}>
            <Trash2 size={16} aria-hidden="true" />
            Delete
          </Button>
        </div>

        <div className="detail-grid">
          <TextField label="Firm" value={record.firm} onChange={(value, immediate) => onFieldChange("firm", value, immediate)} />
          <TextField label="Investor / Contact" value={record.contact} onChange={(value, immediate) => onFieldChange("contact", value, immediate)} />
          <SelectField label="Type" value={record.type} options={uniqueWithCurrent(uniqueValues(records, "type"), record.type)} onChange={(value) => onFieldChange("type", value, true)} />
          <SelectField label="Status" value={record.status} options={uniqueWithCurrent(statusValues(records), record.status)} onChange={(value) => onFieldChange("status", value, true)} />
          <SelectField label="Priority" value={record.priority} options={uniqueWithCurrent(["", ...priorityOrder], record.priority)} onChange={(value) => onFieldChange("priority", value, true)} />
          <TextField label="Round" value={record.round} onChange={(value, immediate) => onFieldChange("round", value, immediate)} />
          <TextField label="Check Size" value={record.checkSize} onChange={(value, immediate) => onFieldChange("checkSize", value, immediate)} />
          <TextField label="Intro / Source" value={record.introSource} onChange={(value, immediate) => onFieldChange("introSource", value, immediate)} />
          <DateField label="Date Contacted" value={record.dateContacted} onChange={(value) => onFieldChange("dateContacted", value, true)} />
          <DateField label="Follow-up Date" value={record.followUpDate} onChange={(value) => onFieldChange("followUpDate", value, true)} />
        </div>

        <TextField
          label="Link / Notes"
          value={record.linkNotes}
          fullWidth
          onChange={(value, immediate) => onFieldChange("linkNotes", value, immediate)}
        />

        {url ? (
          <a className="external-link" href={url} target="_blank" rel="noreferrer">
            Open link
          </a>
        ) : null}

        <AiResearchPanel
          record={record}
          people={people}
          message={researchMessage}
          researchQueue={researchQueue}
          onFieldChange={onFieldChange}
          onResearchSelected={onResearchSelected}
          onAddPerson={onAddPerson}
          onPersonChange={onPersonChange}
          onRemovePerson={onRemovePerson}
        />

        <DiscoveryMetaPanel record={record} />

        <TextareaField label="Next Step" rows={3} value={record.nextStep} onChange={(value, immediate) => onFieldChange("nextStep", value, immediate)} />
        <TextareaField label="Notes" rows={6} value={record.notes} onChange={(value, immediate) => onFieldChange("notes", value, immediate)} />
      </div>
    </aside>
  );
}

function AiResearchPanel({
  record,
  people,
  message,
  researchQueue,
  onFieldChange,
  onResearchSelected,
  onAddPerson,
  onPersonChange,
  onRemovePerson,
}: {
  record: InvestorRecord;
  people: Person[];
  message?: ResearchMessage;
  researchQueue: ResearchQueueState;
  onFieldChange: (field: InvestorField, value: string, immediate?: boolean) => void;
  onResearchSelected: () => void;
  onAddPerson: () => void;
  onPersonChange: (index: number, field: keyof Person, value: string, immediate?: boolean) => void;
  onRemovePerson: (index: number) => void;
}) {
  const isActive = researchQueue.running && researchQueue.activeId === record.id;
  const hasResearch = Boolean(record.aiResearch || record.aiResearchSources);
  const researchedAt = researchUpdatedLabel(record.aiResearchedAt);
  const fitScore = fitScoreLabel(record.aiFitScore) || "No fit score";

  return (
    <section className="ai-research-panel">
      <div className="section-header">
        <div>
          <p className="eyebrow">AI research</p>
          <span className="detail-subtitle">{`${fitScore} / ${researchedAt}`}</span>
        </div>
        <Button disabled={researchQueue.running} onClick={onResearchSelected}>
          <Sparkles size={16} aria-hidden="true" />
          {isActive ? "Researching" : hasResearch ? "Refresh" : "Research"}
        </Button>
      </div>

      {message ? (
        <div className="research-message" data-state={message.state}>
          {message.message}
        </div>
      ) : null}

      <label className="field fit-score-field">
        <span>Fit Score</span>
        <input
          type="number"
          min="1"
          max="10"
          step="1"
          value={record.aiFitScore}
          onChange={(event) => onFieldChange("aiFitScore", event.target.value, false)}
          onBlur={(event) => onFieldChange("aiFitScore", event.target.value, true)}
        />
      </label>

      <PeoplePanel
        people={people}
        onAddPerson={onAddPerson}
        onPersonChange={onPersonChange}
        onRemovePerson={onRemovePerson}
      />

      <TextareaField label="AI Research" rows={8} value={record.aiResearch} onChange={(value, immediate) => onFieldChange("aiResearch", value, immediate)} />
      <TextareaField label="Sources" rows={3} value={record.aiResearchSources} onChange={(value, immediate) => onFieldChange("aiResearchSources", value, immediate)} />
    </section>
  );
}

function DiscoveryMetaPanel({ record }: { record: InvestorRecord }) {
  if (!record.discoveryFoundAt && !record.discoveryReason && !record.discoveryQuery) return null;
  return (
    <section className="discovery-meta-panel">
      <div className="section-header">
        <div>
          <p className="eyebrow">Discovery</p>
          <span className="detail-subtitle">
            {record.discoveryFoundAt ? researchListLabel(record.discoveryFoundAt) : "Saved discovery record"}
          </span>
        </div>
      </div>
      {record.discoveryReason ? (
        <div className="research-message" data-state="ready">
          {record.discoveryReason}
        </div>
      ) : null}
    </section>
  );
}

function PeoplePanel({
  people,
  onAddPerson,
  onPersonChange,
  onRemovePerson,
}: {
  people: Person[];
  onAddPerson: () => void;
  onPersonChange: (index: number, field: keyof Person, value: string, immediate?: boolean) => void;
  onRemovePerson: (index: number) => void;
}) {
  const savedPeople = people.filter((person) => personFields.some((field) => person[field]));

  return (
    <section className="people-panel">
      <div className="section-header">
        <div>
          <p className="eyebrow">People</p>
          <span className="detail-subtitle">{savedPeople.length ? `${savedPeople.length} saved` : "No people saved"}</span>
        </div>
        <Button onClick={onAddPerson}>
          <Plus size={16} aria-hidden="true" />
          Add person
        </Button>
      </div>

      {people.length ? (
        <div className="people-list">
          {people.map((person, index) => (
            <PersonCard
              key={`${index}-${person.name}-${person.email}`}
              person={person}
              index={index}
              onPersonChange={onPersonChange}
              onRemovePerson={onRemovePerson}
            />
          ))}
        </div>
      ) : (
        <div className="empty-state people-empty">
          <strong>No people yet</strong>
          <span>Run research or add a person.</span>
        </div>
      )}
    </section>
  );
}

function PersonCard({
  person,
  index,
  onPersonChange,
  onRemovePerson,
}: {
  person: Person;
  index: number;
  onPersonChange: (index: number, field: keyof Person, value: string, immediate?: boolean) => void;
  onRemovePerson: (index: number) => void;
}) {
  const linkedin = safeUrl(person.linkedin);
  const xProfile = safeUrl(person.xProfile);
  const email = firstEmail(person.email);

  return (
    <div className="person-card">
      <div className="person-card-header">
        <strong>{person.name || `Person ${index + 1}`}</strong>
        <Button variant="danger" size="compact" onClick={() => onRemovePerson(index)}>
          Remove
        </Button>
      </div>

      <div className="person-grid">
        {personFields.map((field) => (
          <TextField
            key={field}
            label={personFieldLabel(field)}
            value={person[field]}
            onChange={(value, immediate) => onPersonChange(index, field, value, immediate)}
          />
        ))}
      </div>

      {linkedin || xProfile || email ? (
        <div className="person-links">
          {linkedin ? (
            <a className="external-link" href={linkedin} target="_blank" rel="noreferrer">
              LinkedIn
            </a>
          ) : null}
          {xProfile ? (
            <a className="external-link" href={xProfile} target="_blank" rel="noreferrer">
              X
            </a>
          ) : null}
          {email ? (
            <a className="external-link" href={`mailto:${email}`}>
              Email
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function DiscoveryWorkbench({
  discoveryBrief,
  discoverySeeds,
  discoveryState,
  spreadsheetState,
  onBriefChange,
  onSeedsChange,
  onFileChange,
  onResearchSpreadsheet,
  onRunDiscovery,
  onOpenRecord,
}: {
  discoveryBrief: string;
  discoverySeeds: string;
  discoveryState: DiscoveryUiState;
  spreadsheetState: SpreadsheetUiState;
  onBriefChange: (value: string) => void;
  onSeedsChange: (value: string) => void;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onResearchSpreadsheet: () => void;
  onRunDiscovery: () => void;
  onOpenRecord: (id: string) => void;
}) {
  return (
    <main className="discovery-page">
      <section className="discovery-workbench" aria-label="Investor discovery">
        <div className="discovery-controls">
          <label className="field full-width">
            <span>Search Brief</span>
            <textarea rows={7} value={discoveryBrief} onChange={(event) => onBriefChange(event.target.value)} />
          </label>

          <label className="field full-width">
            <span>Seed Names or URLs</span>
            <textarea rows={7} value={discoverySeeds} onChange={(event) => onSeedsChange(event.target.value)} />
          </label>

          <label className="field full-width">
            <span>Spreadsheet CSV</span>
            <input type="file" accept=".csv,text/csv" onChange={onFileChange} />
          </label>

          <div className="discovery-actions">
            <Button
              disabled={spreadsheetState.running || discoveryState.running || spreadsheetState.rows.length === 0}
              onClick={onResearchSpreadsheet}
            >
              <Upload size={16} aria-hidden="true" />
              {spreadsheetState.running ? "Researching spreadsheet" : "Research spreadsheet"}
            </Button>
            <span className="research-message" data-state={spreadsheetState.messageState}>
              {spreadsheetState.message}
            </span>
          </div>

          <div className="discovery-actions">
            <Button variant="primary" disabled={discoveryState.running || spreadsheetState.running} onClick={onRunDiscovery}>
              <FileSearch size={16} aria-hidden="true" />
              {discoveryState.running ? "Discovering" : "Discover investors"}
            </Button>
            <span className="research-message" data-state={discoveryState.messageState}>
              {discoveryState.message}
            </span>
          </div>
        </div>

        <section className="discovery-results" aria-label="Discovery results">
          <div className="section-header">
            <div>
              <p className="eyebrow">Discovery results</p>
              <h2>New investor research</h2>
            </div>
          </div>
          {discoveryState.results.length === 0 ? (
            <div className="empty-state">
              <strong>No discovery results yet</strong>
              <span>Ready for the next sourcing run.</span>
            </div>
          ) : (
            <div className="discovery-table">
              <div className="discovery-table-header">
                <span>Name</span>
                <span>Outcome</span>
                <span>Status</span>
                <span>Fit</span>
                <span>Reason</span>
                <span />
              </div>
              {discoveryState.results.map((result) => (
                <div className="discovery-table-row" key={result.id}>
                  <span className="record-name">{result.name}</span>
                  <span>{result.outcome}</span>
                  <span>
                    <StatusPill status={result.status} />
                  </span>
                  <span>{fitScoreLabel(result.fitScore) || "No score"}</span>
                  <span className="list-cell">{result.reason || "No reason saved"}</span>
                  <Button size="compact" onClick={() => onOpenRecord(result.id)}>
                    Open
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>
      </section>
    </main>
  );
}

function TextField({
  label,
  value,
  fullWidth = false,
  onChange,
}: {
  label: string;
  value: string;
  fullWidth?: boolean;
  onChange: (value: string, immediate: boolean) => void;
}) {
  return (
    <label className={`field ${fullWidth ? "full-width" : ""}`}>
      <span>{label}</span>
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value, false)}
        onBlur={(event) => onChange(event.target.value, true)}
      />
    </label>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <input type="date" value={dateValue(value)} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option || "empty"} value={option}>
            {option || "Unspecified"}
          </option>
        ))}
      </select>
    </label>
  );
}

function TextareaField({
  label,
  value,
  rows,
  onChange,
}: {
  label: string;
  value: string;
  rows: number;
  onChange: (value: string, immediate: boolean) => void;
}) {
  return (
    <label className="field full-width">
      <span>{label}</span>
      <textarea
        rows={rows}
        value={value}
        onChange={(event) => onChange(event.target.value, false)}
        onBlur={(event) => onChange(event.target.value, true)}
      />
    </label>
  );
}

function uniqueWithCurrent(options: string[], current: string): string[] {
  return current ? [...new Set([...options, current])] : [...new Set([current, ...options])];
}

function personFieldLabel(field: keyof Person): string {
  if (field === "xProfile") return "X Profile";
  return field.charAt(0).toUpperCase() + field.slice(1);
}
